import { Injectable } from '@angular/core';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { KeycloakRoles, KeycloakProfile } from 'keycloak-js';
import { environment } from '../../environments/environment';
import { Subject, interval, Subscription, of, zip, ReplaySubject } from 'rxjs';
import { delay } from 'rxjs/operators';
import { UpdateTokenEvent } from './update-token-event';
import { UpdateTokenAction } from './update-token-action';
import { UserProfile } from '../common/model/user-profile';

@Injectable({
  providedIn: 'root'
})
export class KeycloakInit {

  private updateTokenInterval = new Subscription();
  private updateTokenTimeoutSubscription = new Subscription();
  private updateInProgress = false;
  private updateTokenSubject = new Subject<UpdateTokenEvent>();
  updateToken$ = this.updateTokenSubject.asObservable();
  private updateUserProfileSubscription = new ReplaySubject<UserProfile>();
  updateUserProfile$ = this.updateUserProfileSubscription.asObservable();

  constructor(private keycloakService: KeycloakService) { }

  initializer(): Promise<any> {
    this.subscribeKeycloakEvents();

    return new Promise(async (resolve, reject) => {
      try {
        await this.keycloakService.init({
          config: {
            url: environment.keycloakAuthUrl,
            realm: environment.keycloakRealm,
            clientId: environment.keycloakClientId
          },
          initOptions: {
            onLoad: 'login-required',
            checkLoginIframe: false
          },
          loadUserProfileAtStartUp: false,
          enableBearerInterceptor: false
        });
        resolve(null);
      } catch (error) {
        reject(error);
      }
    });
  }

  public getKeycloakService(): KeycloakService {
    return this.keycloakService;
  }

  public getToken(): string {
    return this.keycloakService.getKeycloakInstance().token;
  }

  public refresh(): Promise<boolean> {
    return this.keycloakService.updateToken(-1);
  }

  public updateToken(action: UpdateTokenAction): void {
    if (!this.updateInProgress) {
      this.updateInProgress = true;
      this.updateTokenSubject.next(UpdateTokenEvent.IN_PROGRESS);

      if (action == UpdateTokenAction.UPDATE_EXPIRED)
        this.subscribeUpdateTokenTimeout();

      this.keycloakService.updateToken()
        .catch(() => {
          if (action == UpdateTokenAction.UPDATE_EXPIRED)
            this.addUpdateTokenInterval();
          this.sendUpdateError(action);
          this.updateInProgress = false;
        });
    }
  }

  private sendUpdateError(action: UpdateTokenAction): void {
    let event: UpdateTokenEvent = UpdateTokenEvent.EXPIRED_ERROR;
    if (action == UpdateTokenAction.UPDATE_MANUAL)
      event = UpdateTokenEvent.MANUAL_ERROR;
    if (action == UpdateTokenAction.UPDATE_INTERVAL)
      event = UpdateTokenEvent.INTERVAL_ERROR;
    this.updateTokenSubject.next(event);
  }

  private subscribeKeycloakEvents(): void {
    this.keycloakService.keycloakEvents$.subscribe(keycloakEvent => {
      switch (keycloakEvent.type) {
        case KeycloakEventType.OnReady:
          zip(
            this.keycloakService.loadUserProfile(),
            of(this.keycloakService.getKeycloakInstance().resourceAccess[environment.keycloakClientId])
          ).subscribe(
            ([profile, resourceAccess]: [KeycloakProfile, KeycloakRoles?]) => {
              this.updateUserProfileSubscription.next(
                new UserProfile(profile.id, profile.username, resourceAccess?.roles || [], profile.firstName, profile.lastName, profile.email)
              );
            }
          );
          break;

        case KeycloakEventType.OnTokenExpired:
          this.updateInProgress = false;
          this.updateToken(UpdateTokenAction.UPDATE_EXPIRED);
          break;

        case KeycloakEventType.OnAuthRefreshSuccess:
          this.updateTokenSubject.next(UpdateTokenEvent.SUCCESS);
          this.updateInProgress = false;
          this.unsubscribeUpdateTokenTimeout();
          this.deleteUpdateTokenInterval();
          break;
      }
    });

  }

  private subscribeUpdateTokenTimeout(): void {
    this.updateTokenTimeoutSubscription = of('')
      .pipe(delay(environment.updateTokenWaitingTime))
      .subscribe(() => this.updateTokenSubject.next(UpdateTokenEvent.TIMEOUT));
  }

  private unsubscribeUpdateTokenTimeout(): void {
    this.updateTokenTimeoutSubscription.unsubscribe();
  }

  private addUpdateTokenInterval(): void {
    this.updateTokenInterval = interval(environment.refreshTokenRetryInterval)
      .subscribe(() => this.updateToken(UpdateTokenAction.UPDATE_INTERVAL));
  }

  private deleteUpdateTokenInterval(): void {
    this.updateTokenInterval.unsubscribe();
  }
}
